import React, { useEffect, useMemo, useState } from 'react';
import { Segmented } from 'antd';

import { DataState, DATA_STATE } from '../loading';
import { Chart } from '../charts/LazyCharts';
import { getFormVolume } from '../../services/publicForms';
import {
	VOLUME_PERIODS, volumeIsQuiet, volumeResult, volumeSentence, volumeSpec,
} from '../../utils/formVolume';

/**
 * How much a form took, over time (KPR-513; ruling f03's "a manager sees
 * submission volume per form, for both kinds"): a permanent public form's
 * (`source.kind` 'form') or an invite link's ('link'). Drawn by the charts
 * renderer as a line of Submitted, Accepted and Rejected (utils/formVolume.js
 * says why a line), with its table twin one button away; the bot-check
 * failures and the limits' refusals in a sentence under it.
 *
 * Rendered INLINE, under the form's row, never in a dialog: a chart is drawn
 * on `--surface`, where the data palette is measured (standards §12, "Charts
 * are drawn on --surface"), and a dialog is `--surface-raised`.
 *
 * An API without the volume routes (one older than KPR-512) answers 404; the
 * callers only offer this where the public-forms list answered, and it says
 * so in a line if it meets one anyway.
 */
export default function FormVolume({ collectionId, source, name }) {
	const [days, setDays] = useState(30);
	const [volume, setVolume] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showTable, setShowTable] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const sourceKind = source?.kind;
	const sourceId = source?.id;

	useEffect(() => {
		if (!collectionId || !sourceId) return undefined;
		let active = true;
		setLoading(true);
		setError(null);
		getFormVolume(collectionId, { kind: sourceKind, id: sourceId }, days)
			.then((data) => { if (active) setVolume(data); })
			.catch((err) => {
				if (!active) return;
				setVolume(null);
				setError(err?.response?.status === 404
					? 'Volume is not available on this keepr server yet.'
					: (err?.response?.data?.message || 'The volume could not be loaded. Try again in a moment.'));
			})
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [collectionId, sourceKind, sourceId, days, attempt]);

	const result = useMemo(() => volumeResult(volume), [volume]);
	const chart = useMemo(() => ({
		name: `Submissions${name ? ` to ${name}` : ''}`,
		spec: volumeSpec(volume?.granularity),
	}), [name, volume]);
	const sentence = volumeSentence(volume);
	const quiet = volume && volumeIsQuiet(volume);

	return (
		<div className="form-volume" data-testid="form-volume">
			<div className="form-volume__head">
				<Segmented
					size="small"
					value={days}
					options={VOLUME_PERIODS}
					onChange={setDays}
					aria-label="Volume over"
				/>
			</div>
			{error ? (
				<DataState
					className="form-volume__state"
					state={DATA_STATE.ERROR}
					error={error}
					onRetry={() => setAttempt(n => n + 1)}
					label="volume"
				/>
			) : (
				<>
					<Chart
						chart={chart}
						result={result}
						loading={loading}
						collectionId={collectionId}
						cardName="submission"
						height={220}
						headingLevel={4}
						showTable={showTable}
						onToggleTable={() => setShowTable(on => !on)}
					/>
					{quiet ? (
						<p className="form-volume__note">
							{sourceKind === 'link'
								? 'Nothing was sent in this time. Send the link to the people who should fill it in.'
								: 'Nothing was sent in this time. Put the address, or its QR code, where people will see it.'}
						</p>
					) : null}
					{sentence ? <p className="form-volume__note" data-testid="form-volume-more">{sentence}</p> : null}
				</>
			)}
		</div>
	);
}
